import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { canAccessResource, canAccessSome, PrivilegeName, PrivilegeScope } from "../../../helpers/privilege";

type ProctorGroup = { id: number; name: string };

export type CurrentUser = {
	id: string;
	instituteId: string;
	privileges: Record<PrivilegeName | string, PrivilegeScope[]>;
	proctorGroups: ProctorGroup[];
};

export type CurrentAccount = {
	id: string;
	email?: string;
	currentUser?: CurrentUser;
};

export type CurrentUserState = {
	account?: CurrentAccount;
};

const initialState: CurrentUserState = {};

const currentUserSlice = createSlice({
	name: "currentUser",
	initialState,
	reducers: {
		setCurrentAccountInfo: (state, { payload }: PayloadAction<CurrentAccount | undefined>) => {
			state.account = payload;
		},
	},
	selectors: {
		selectCurrentAccount: (state) => state.account,
		selectCanAccessInstall: (state) =>
			state.account?.currentUser &&
			canAccessResource(state.account.currentUser, { privilege: "INSTITUTE", action: "DELETE", resourceScope: {} }), // See 'canCreateInstitute' in management/containers/InstituteDeploymentEditor.tsx
		selectCanAccessApplications: (state) =>
			state.account?.currentUser &&
			canAccessSome(state.account.currentUser, { privilege: "APPLICATION" }),
		selectCanAccessLiveProctoring: (state) =>
			state.account?.currentUser &&
			state.account.currentUser.proctorGroups.length > 0,
		selectCanAccessFeedback: (state) =>
			state.account?.currentUser &&
			canAccessSome(state.account.currentUser, { privilege: "FEEDBACK" }),
		selectCanAccessEventBanners: (state) =>
			state.account?.currentUser &&
			canAccessSome(state.account.currentUser, { privilege: "EVENT_BANNER" }),
	},
});

export const selectCanEditInstitution = (instituteId: string) => (state: any) => {
	const account = currentUserSlice.selectors.selectCurrentAccount(state);
	return account?.currentUser && canAccessResource(account.currentUser, {
		privilege: "INSTITUTE",
		action: "UPDATE",
		resourceScope: { instituteId }
	});
};

export default currentUserSlice;
